export type AppointmentApiType =
  | 'ad hoc'
  | 'combo'
  | 'eri tracking'
  | 'heart failure'
  | 'in clinic'
  | 'physician request'
  | 'routine';

const APPOINTMENT_API_TYPES: Record<string, AppointmentApiType> = {
  'ad hoc': 'ad hoc',
  combo: 'combo',
  'eri tracking': 'eri tracking',
  'heart failure': 'heart failure',
  'in clinic': 'in clinic',
  'physician request': 'physician request',
  routine: 'routine'
};

export function appointmentTypeKey(type?: string | null): string {
  return (type ?? '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
}

/** Exact type value the appointment API accepts. */
export function appointmentApiType(type?: string | null): AppointmentApiType | '' {
  return APPOINTMENT_API_TYPES[appointmentTypeKey(type)] ?? '';
}

type ScheduleTerm = { termDays?: number | string | null } | null | undefined;

export interface ClinicScheduleSource {
  pmScheduleFrequency?: ScheduleTerm;
  icdScheduleFrequency?: ScheduleTerm;
  ilrScheduleFrequency?: ScheduleTerm;
  crtdScheduleFrequency?: ScheduleTerm;
  crtpScheduleFrequency?: ScheduleTerm;
  eriScheduleFrequency?: ScheduleTerm;
  settings?: {
    scheduling?: {
      cycleHf?: number | null;
      eriCadence?: number | null;
    } | null;
  } | null;
}

export interface AppointmentFrequencyInput {
  type: AppointmentApiType;
  deviceType?: string | null;
  patientCycleIcdPm?: number | null;
  patientCycleHf?: number | null;
  patientCycleIlr?: number | null;
  clinicRoutineDays?: number | null;
  clinicHfDays?: number | null;
  clinicEriDays?: number | null;
  /** Physician-requested interval the user typed. Omitted means 0. */
  enteredFrequency?: number | null;
}

/** A stored 0 means the patient has no override. */
function positive(value?: number | string | null): number | undefined {
  if (value == null || value === '') return undefined;
  const number = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(number) || number <= 0) return undefined;
  return number;
}

function termDays(term: ScheduleTerm): number | undefined {
  return positive(term?.termDays);
}

/** Clinic routine cadence for this device type. Heart failure is separate. */
export function clinicRoutineDays(
  organization: ClinicScheduleSource | null | undefined,
  deviceType?: string | null
): number | undefined {
  const type = (deviceType ?? '').trim().toUpperCase();
  if (type === 'CRT-D' || type === 'CRTD') return termDays(organization?.crtdScheduleFrequency);
  if (type === 'CRT-P' || type === 'CRTP') return termDays(organization?.crtpScheduleFrequency);
  if (type === 'ICD') return termDays(organization?.icdScheduleFrequency);
  if (type === 'ILR') return termDays(organization?.ilrScheduleFrequency);
  if (type === 'PM') return termDays(organization?.pmScheduleFrequency);
  return undefined;
}

export function clinicHeartFailureDays(
  organization: ClinicScheduleSource | null | undefined
): number | undefined {
  return positive(organization?.settings?.scheduling?.cycleHf);
}

export function clinicEriDays(
  organization: ClinicScheduleSource | null | undefined
): number | undefined {
  return (
    termDays(organization?.eriScheduleFrequency) ??
    positive(organization?.settings?.scheduling?.eriCadence)
  );
}

/**
 * Integer frequency for a new appointment.
 * In clinic, ad hoc, and physician request are 0 unless the user entered a physician interval.
 * Routine and combo use the patient cycle, then the clinic device frequency, then 31 or 91.
 * Heart failure uses the patient cycle, then the clinic heart failure cycle, then 31.
 */
export function resolveAppointmentFrequency(input: AppointmentFrequencyInput): number {
  if (input.type === 'in clinic' || input.type === 'ad hoc') return 0;
  if (input.type === 'physician request') {
    if (input.enteredFrequency == null || !Number.isFinite(input.enteredFrequency)) return 0;
    return input.enteredFrequency < 0 ? 0 : input.enteredFrequency;
  }
  if (input.type === 'eri tracking') return input.clinicEriDays ?? 35;
  if (input.type === 'heart failure') {
    return positive(input.patientCycleHf) ?? input.clinicHfDays ?? 31;
  }
  const ilr = (input.deviceType ?? '').trim().toUpperCase() === 'ILR';
  if (input.type === 'routine' && ilr) {
    return positive(input.patientCycleIlr) ?? input.clinicRoutineDays ?? 31;
  }
  return positive(input.patientCycleIcdPm) ?? input.clinicRoutineDays ?? 91;
}

/** A failed patient or clinic load must not fall through to 31 or 91. */
export function appointmentSettingsStatus(input: {
  hasPatientId: boolean;
  patientSuccess: boolean;
  patientError: boolean;
  hasClinicId: boolean;
  clinicSuccess: boolean;
  clinicError: boolean;
}): { ready: boolean; failed: boolean } {
  if (!input.hasPatientId || input.patientError) {
    return { ready: false, failed: input.patientError };
  }
  if (!input.patientSuccess) return { ready: false, failed: false };
  if (!input.hasClinicId) return { ready: true, failed: false };
  if (input.clinicError) return { ready: false, failed: true };
  if (!input.clinicSuccess) return { ready: false, failed: false };
  return { ready: true, failed: false };
}

export function appointmentCreateBody(input: {
  deviceId?: string;
  patientId?: string;
  modelNumber: string;
  serialNumber: string;
  type: AppointmentApiType;
  scheduledDate: string;
  frequency: number;
  appointmentNotes?: string;
}) {
  const notes = input.appointmentNotes?.trim();
  return {
    deviceId: input.deviceId,
    patientSFId: input.patientId,
    modelNumber: input.modelNumber,
    serialNumber: input.serialNumber,
    type: input.type,
    frequency: input.frequency,
    scheduledDate: input.scheduledDate,
    scheduleDate: input.scheduledDate,
    ...(notes ? { appointmentNotes: notes } : {})
  };
}
